import type {
  DownloadFullReportRequestDto,
  GetConfigResponseDto,
} from 'shared/dtos/reports.dto';
import type { JWT } from 'shared/interfaces/jwt.interface';

import { jwtDecode } from 'jwt-decode';
import socketClient from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

import config from '../config';
import { ApiService } from './api.service';
import events from '../shared/constants/events';
import * as utils from '../shared/utils';

export default class ReportService extends ApiService {
  private baseUrl = `/reports`;
  private socket = socketClient(config.socketURL, {
    autoConnect: false,
  });

  public getFullReport = async () => {
    return await this.get(this.baseUrl);
  };

  public getConfigReport = async (): Promise<GetConfigResponseDto> => {
    return await this.get(`${this.baseUrl}/config-report`);
  };

  public getShortReport = async (id, entityName) => {
    return await this.get(`${this.baseUrl}/${entityName}/${id}`);
  };

  private openConnection = (sessionId): void => {
    if (this.socket.connected) {
      return;
    }

    const token = this.getToken() || '';
    const decodedToken = jwtDecode(token) as JWT;
    const userId = decodedToken.id;

    this.socket.on(`pdf-${userId}-${sessionId}`, (data) => {
      try {
        const url = JSON.parse(data).url;
        utils.openExternalLink(url);
        this.socket.close();
      } catch {
        this.socket.close();
      }
      document.body.dispatchEvent(events.UNLOCK.event);
    });

    this.socket.open();
  };

  private getPDF = (downloadFunc): void => {
    const sessionId = uuidv4();
    this.openConnection(sessionId);
    downloadFunc(sessionId);
  };

  public downloadFullReport = (
    type,
    data?: DownloadFullReportRequestDto,
  ): void => {
    this.getPDF((sessionId) =>
      this.post(
        `${this.baseUrl}/pdf?sessionId=${sessionId}&type=${type}`,
        data,
      ),
    );
  };

  public downloadDivisionReport = (divisionId, type): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/division-pdf/${divisionId}?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };

  public downloadDivisionsReport = (type): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/divisions-pdf?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };

  public downloadAgencyReport = (agencyId, type): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/agency-pdf/${agencyId}?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };

  public downloadAgenciesReport = (type): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/agencies-pdf?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };

  public downloadPolicyReport = (policyId): void => {
    this.getPDF((sessionId) =>
      this.get(`${this.baseUrl}/policy-pdf/${policyId}?sessionId=${sessionId}`),
    );
  };

  public downloadPoliciesReport = (type): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/policies-pdf?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };

  public downloadShortPolicyReportByPolicyID = ({ policyId, type }): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/short-policy-pdf/${policyId}?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };

  public downloadShortPoliciesReport = (type): void => {
    this.getPDF((sessionId) =>
      this.get(
        `${this.baseUrl}/short-policies-pdf?sessionId=${sessionId}&type=${type}`,
      ),
    );
  };
}
